import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowRight, ShieldCheck, CheckCircle2, Sparkles, Info, Pencil, ChevronDown } from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { MoneyLandedModal } from "@/components/MoneyLandedModal";
import { PaymentReviewModal } from "@/components/PaymentReviewModal";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/format";

const formatDate = (iso) => {
  if (!iso) return "your next income";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "your next income";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function EmptyState() {
  const [open, setOpen] = useState(false);
  return (
    <div className="fade-up mx-auto max-w-md py-10 text-center" data-testid="sprint-empty-state">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#DDE7DF] text-[#164C3B]">
        <Zap className="h-6 w-6" />
      </span>
      <h1 className="mt-5 font-serif text-3xl text-[#171717]">No active Sprint</h1>
      <p className="mt-2 text-[#525252]">
        When money comes in, start here and we'll show you what may be available for your next Sprint.
      </p>
      <Button
        onClick={() => setOpen(true)}
        data-testid="empty-money-landed-trigger"
        className="mt-6 min-h-12 rounded-xl bg-[#2F6B57] px-6 text-base font-semibold text-white hover:bg-[#255643]"
      >
        Feeling rich? 💰
      </Button>
      <MoneyLandedModal open={open} onOpenChange={setOpen} />
    </div>
  );
}

function Allocations({ sprint }) {
  return (
    <div className="space-y-3" data-testid="debt-recommendations-list">
      {sprint.allocations.map((a) => (
        <div
          key={a.debt_id}
          className="rounded-2xl pe-surface p-5"
          data-testid={`recommendation-row-${a.debt_id}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-serif text-xl text-[#171717]">
                {a.eliminated ? "Pay off" : "Apply to"} {a.name}
              </p>
              <p className="text-sm text-[#70584A]">You owe {fmt(a.current_balance)}</p>
            </div>
            <p className="font-serif text-2xl text-[#2F6B57]" data-testid={`recommended-${a.debt_id}`}>
              {fmt(a.recommended)}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-[#F0EBE3] pt-3 text-sm">
            <span className="text-[#525252]">Projected balance</span>
            <span className="flex items-center gap-2 font-semibold text-[#171717]">
              {fmt(a.projected_balance)}
              {a.eliminated && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#DDE7DF] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#164C3B]">
                  <CheckCircle2 className="h-3 w-3" /> Paid off
                </span>
              )}
            </span>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between rounded-xl bg-[#F5F2EC] px-5 py-3.5">
        <span className="text-sm font-medium text-[#164C3B]">Total to pay</span>
        <span className="font-serif text-xl text-[#164C3B]" data-testid="sprint-total-recommended">
          {fmt(sprint.total_recommended)}
        </span>
      </div>

      {sprint.unassigned > 0 && (
        <div className="flex items-start gap-2 rounded-xl pe-surface px-5 py-3.5 text-sm text-[#70584A]" data-testid="sprint-unassigned">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
          <span>
            <span className="font-semibold text-[#171717]">{fmt(sprint.unassigned)}</span> is left over. We keep it safe for you instead of forcing it onto another debt.
          </span>
        </div>
      )}

      <p className="px-1 text-xs text-[#70584A]">These are projected balances only.</p>
    </div>
  );
}

function ProposedSprint({ sprint }) {
  const { accept, protect } = useAppState();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [showBills, setShowBills] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const readyToSprint = async () => {
    setBusy(true);
    try {
      await accept(sprint.id); // -> customer_accepted; parent re-renders to ActiveSprint
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const keepProtected = async () => {
    setBusy(true);
    try {
      await protect(sprint.id);
      toast.success("Money kept safe. Nothing changed.");
      navigate("/app");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="sprint-reveal-view">
      <div className="text-center sm:text-left">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]">
          <Sparkles className="h-3.5 w-3.5" /> Proposed
        </span>
        <h1 className="mt-3 font-serif text-4xl leading-tight text-[#171717] sm:text-5xl">
          Here's your proposed Sprint.
        </h1>
        <p className="mt-2 text-[#525252]">
          Based on {fmt(sprint.foundation_breakdown?.income_received ?? sprint.landed_money)} and your current setup.
        </p>
      </div>

      {/* Potential Sprint Money hero with simple breakdown */}
      <div className="rounded-2xl border border-[#164C3B] bg-[#164C3B] p-7 text-white shadow-[0_8px_30px_-8px_rgba(22,76,59,0.5)]">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">Feeling rich?</p>
          <button
            type="button"
            data-testid="change-amount-button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/20"
          >
            <Pencil className="h-3 w-3" /> Change amount
          </button>
        </div>
        <p className="mt-1 font-serif text-3xl tracking-tight text-white" data-testid="landed-amount-display">
          {fmt(sprint.foundation_breakdown?.income_received ?? sprint.landed_money)}
        </p>

        {sprint.foundation_breakdown && (
          <div className="mt-4 space-y-2 border-t border-white/15 pt-4 text-sm text-white/80" data-testid="coverage-breakdown">
            <p className="text-xs font-medium text-white/60" data-testid="coverage-until-label">
              Keeping available until {formatDate(sprint.foundation_breakdown.next_income_date)}
            </p>
            <button
              type="button"
              data-testid="toggle-bills-breakdown"
              onClick={() => setShowBills((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg py-1 text-left transition-colors hover:text-white"
            >
              <span className="flex items-center gap-1.5">
                Bills &amp; essentials
                <ChevronDown className={`h-4 w-4 transition-transform ${showBills ? "rotate-180" : ""}`} />
              </span>
              <span data-testid="breakdown-essentials">− {fmt(sprint.foundation_breakdown.period_essentials)}</span>
            </button>
            {showBills && (
              <div className="ml-1 space-y-1.5 border-l border-white/15 pl-3 pt-1 text-[13px] text-white/70" data-testid="bills-breakdown-detail">
                {(sprint.foundation_breakdown.essentials_detail || []).length === 0 && (
                  <p>No essentials due before your next income.</p>
                )}
                {(sprint.foundation_breakdown.essentials_detail || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between gap-3" data-testid={`bill-item-${i}`}>
                    <span>
                      {e.name}
                      <span className="text-white/45"> · due {formatDate(e.due_dates?.[0])}{e.count > 1 ? ` (×${e.count})` : ""}</span>
                    </span>
                    <span>− {fmt(e.reserved)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span>Everyday spending</span>
              <span data-testid="breakdown-living">− {fmt(sprint.foundation_breakdown.living_bucket)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Required debt payments</span>
              <span data-testid="breakdown-minimums">− {fmt(sprint.foundation_breakdown.period_debt_minimums)}</span>
            </div>
          </div>
        )}

        <div className="mt-4 border-t border-white/15 pt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">You can Sprint</p>
          <p
            className="mt-1 font-serif text-5xl tracking-tight text-[#C9A86A] sm:text-6xl"
            data-testid="potential-sprint-money-display"
          >
            {fmt(sprint.potential_sprint_money)}
          </p>
          <p className="mt-3 text-sm text-white/70">
            That's what's left after covering the numbers you've asked us to protect.
          </p>
        </div>
      </div>

      <Allocations sprint={sprint} />

      <p className="px-1 text-sm leading-relaxed text-[#525252]" data-testid="coverage-explanation">
        We looked at what needs to be covered before your next expected income. What's left may be available for your Sprint.
      </p>

      <div className="flex items-start gap-2.5 rounded-xl bg-[#DDE7DF] p-4 text-sm leading-relaxed text-[#164C3B]">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
        <span>
          <strong>This is only a suggestion. You decide.</strong> Nothing changes until you tell us you've made a payment.
        </span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        <Button
          data-testid="confirm-sprint-decisions-button"
          onClick={readyToSprint}
          disabled={busy}
          className="min-h-12 flex-1 rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
        >
          I'm ready to Sprint
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          data-testid="keep-protected-button"
          onClick={keepProtected}
          disabled={busy}
          className="min-h-12 flex-1 rounded-xl border-[#E5E0D9] bg-white text-base font-medium text-[#70584A] hover:bg-[#F5F2EC]"
        >
          Keep it protected
        </Button>
      </div>

      <MoneyLandedModal open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}

function ActiveSprint({ sprint, onCompleted }) {
  const { protect } = useAppState();
  const navigate = useNavigate();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const keepProtected = async () => {
    setBusy(true);
    try {
      await protect(sprint.id);
      toast.success("Money kept safe. Nothing changed.");
      navigate("/app");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="sprint-active-view">
      <div className="text-center sm:text-left">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F2EC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">
          <Zap className="h-3.5 w-3.5 text-[#C9A86A]" /> Sprint in progress
        </span>
        <h1 className="mt-3 font-serif text-4xl leading-tight text-[#171717] sm:text-5xl">
          Sprint in progress
        </h1>
        <p className="mt-2 text-[#525252]">
          Your {fmt(sprint.potential_sprint_money)} Sprint is ready. Review and confirm when you've made the payment.
        </p>
      </div>

      <Allocations sprint={sprint} />

      <div className="flex items-start gap-2.5 rounded-xl bg-[#DDE7DF] p-4 text-sm leading-relaxed text-[#164C3B]">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
        <span>No balances will change until you confirm the payment you actually made.</span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        <Button
          data-testid="review-and-confirm-button"
          onClick={() => setReviewOpen(true)}
          className="min-h-12 flex-1 rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
        >
          Review and confirm
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          data-testid="keep-protected-button"
          onClick={keepProtected}
          disabled={busy}
          className="min-h-12 flex-1 rounded-xl border-[#E5E0D9] bg-white text-base font-medium text-[#70584A] hover:bg-[#F5F2EC]"
        >
          Keep it protected
        </Button>
      </div>

      <PaymentReviewModal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        sprint={sprint}
        onCompleted={onCompleted}
      />
    </div>
  );
}

function SprintComplete({ sprint }) {
  const navigate = useNavigate();
  const eliminated = (sprint.confirmed_payments || []).filter((p) => p.eliminated);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="scale-in mx-auto max-w-xl py-4 text-center" data-testid="sprint-complete-card">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#DDE7DF] text-[#2F6B57]">
        <CheckCircle2 className="h-8 w-8" strokeWidth={2.2} />
      </span>
      <h1 className="mt-5 font-serif text-4xl text-[#171717] sm:text-5xl">
        Sprint Complete!
      </h1>

      <div className="mx-auto mt-6 max-w-sm rounded-2xl pe-surface p-6">
        <p className="font-serif text-5xl tracking-tight text-[#C9A86A]" data-testid="debt-attacked-amount">
          {fmt(sprint.total_attacked)}
        </p>
        <p className="mt-1 text-sm font-medium uppercase tracking-wider text-[#70584A]">attacked</p>
      </div>

      <div className="mt-5 space-y-2.5 text-left">
        {(sprint.confirmed_payments || []).map((p) => (
          <div
            key={p.debt_id}
            className="flex items-center justify-between rounded-xl pe-surface px-4 py-3"
            data-testid={`complete-row-${p.debt_id}`}
          >
            <span className="font-medium text-[#171717]">{p.name}</span>
            <span className="flex items-center gap-2 text-sm text-[#525252]">
              {fmt(p.previous_balance)}
              <ArrowRight className="h-3.5 w-3.5 text-[#70584A]" />
              <span className="font-semibold text-[#164C3B]">{fmt(p.new_balance)}</span>
            </span>
          </div>
        ))}
      </div>

      {eliminated.length > 0 && (
        <div className="mt-4 space-y-2">
          {eliminated.map((p) => (
            <p
              key={p.debt_id}
              className="inline-flex items-center gap-2 rounded-full bg-[#DDE7DF] px-4 py-1.5 text-sm font-semibold text-[#164C3B]"
              data-testid={`eliminated-badge-${p.debt_id}`}
            >
              <CheckCircle2 className="h-4 w-4 text-[#2F6B57]" /> {p.name} paid off ✓
            </p>
          ))}
        </div>
      )}

      <div className="mt-7 flex flex-col gap-3 sm:flex-row-reverse sm:justify-center">
        <Button
          data-testid="view-progress-button"
          onClick={() => navigate("/progress")}
          className="min-h-12 rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643] sm:px-8"
        >
          View my progress
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          data-testid="go-to-home-button"
          onClick={() => navigate("/app")}
          className="min-h-12 rounded-xl border-[#E5E0D9] bg-white text-base font-medium text-[#70584A] hover:bg-[#F5F2EC] sm:px-8"
        >
          Go to home
        </Button>
      </div>
    </div>
  );
}

function CoveredState({ sprint }) {
  const { protect } = useAppState();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const done = async () => {
    try {
      await protect(sprint.id);
    } catch (e) {
      /* no-op */
    }
    navigate("/app");
  };

  return (
    <div className="fade-up mx-auto max-w-md py-10 text-center" data-testid="sprint-covered-view">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#DDE7DF] text-[#2F6B57]">
        <CheckCircle2 className="h-8 w-8" strokeWidth={2.2} />
      </span>
      <h1 className="mt-5 font-serif text-4xl text-[#171717]">You're covered. ✓</h1>
      <p className="mt-3 text-[#525252]">
        Your money already has jobs this pay period, so there's nothing available to Sprint right now.
      </p>
      <p className="mt-2 text-sm text-[#70584A]">
        That's okay. We'll check again when money lands next time.
      </p>
      <Button
        data-testid="covered-home-button"
        onClick={done}
        className="mt-6 min-h-12 rounded-xl bg-[#2F6B57] px-8 text-base font-semibold text-white hover:bg-[#255643]"
      >
        Back to home
      </Button>
    </div>
  );
}

export default function MySprint() {
  const { state } = useAppState();
  const [completed, setCompleted] = useState(null);
  if (!state) return null;

  if (completed) return <SprintComplete sprint={completed} />;

  const active = state.active_sprint;
  if (active && active.status === "recommended") {
    if (Number(active.potential_sprint_money) <= 0) return <CoveredState sprint={active} />;
    return <ProposedSprint sprint={active} />;
  }
  if (active && ["customer_accepted", "awaiting_confirmation"].includes(active.status)) {
    return <ActiveSprint sprint={active} onCompleted={setCompleted} />;
  }
  return <EmptyState />;
}
